import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  semAssinatura,
  trechoParaCitar,
  type AcaoDaMensagem,
  type ChamadoDetalheDto,
  type ConversaDto,
  type MensagemCitadaDto,
  type MensagemDto,
  type UsuarioSessao,
} from '@crm/shared'
import { ErroRequisicao } from '../../api/cliente'
import { useEquipe } from '../../api/hooks'
import { Avatar } from '../../componentes/Avatar'
import { BarraComTransbordo, type AcaoDaBarra } from '../../componentes/BarraComTransbordo'
import { BotaoIcone } from '../../componentes/BotaoIcone'
import { GravadorDeAudio } from '../../componentes/GravadorDeAudio'
import { NomeDoContato } from '../../componentes/NomeDoContato'
import { SeletorEmoji } from '../../componentes/SeletorEmoji'
import { EscolherAgendamento } from './EscolherAgendamento'
import { EtapasDoFunil } from './EtapasDoFunil'
import { FaixaDoPedido, ModalFecharParaMim } from './Apoio'
import { FichaDoContato } from './FichaDoContato'
import { EnviosPendentes } from './EnviosPendentes'
import { EditarMensagem } from './EditarMensagem'
import { MenuDaMensagem, copiarTexto, opcoesDaMensagem, type OpcaoDoMenu } from './MenuDaMensagem'
import { FaixaDeAnuncio } from './FaixaDeAnuncio'
import { EtiquetasDoAtendimento, InfoDoAtendimento } from './EtiquetasDoAtendimento'
import { agendarEnvio, type CitacaoDoEnvio } from '../../envio/fila-de-envio'
import { TextoWhatsApp } from '../../componentes/TextoWhatsApp'
import {
  useApagarMensagem,
  useEnviarMensagem,
  useEnviarMidia,
  useReabrirChamado,
  useMensagens,
  useOcorrencias,
  useRemoverApoio,
  useRetomei,
} from '../../api/hooks-chat'
import { NavegarNaBusca } from './NavegarNaBusca'
import { useCampos, useContato, useFunis, useNovaNegociacao, useTags } from '../../api/hooks-leads'
import {
  IconeApoio,
  IconeEncerrar,
  IconeEnviar,
  IconeEtiqueta,
  IconeLixeira,
  IconeMensagemAgendada,
  IconeNegociacao,
  IconeModeloAprovado,
  IconeNotaInterna,
  IconeReabrir,
  IconeSair,
  IconeSetaBaixo,
  IconeTransferir,
  IconeX,
} from '../../componentes/icones'
import { usePerguntar } from '../../componentes/perguntar'
import { pode } from '../../permissao'
import { Aviso, Botao, Carregando, Modal, juntar } from '../../componentes/ui'
import { ModalContato } from '../leads/ModalContato'
import { JanelaDeEtiquetas, ModalTarefa } from './AcoesDoContato'
import { formatarDataHora } from '../../formato'
import { ModalAgendar, SeletorAnexo, SeletorRespostas } from './ExtrasDoChat'
import { BotaoMateriais, tamanhoLegivel } from './Materiais'
import { FaixaDaJanela, JanelaDeModelos, useAgoraDoMinuto } from './ModelosAprovados'
import { PlayerDeVideo } from '../../componentes/PlayerDeVideo'
import {
  VisualizadorDeMidia,
  midiaDaMensagem,
  type MidiaParaVer,
} from '../../componentes/VisualizadorDeMidia'

/** "imagem" | "áudio" | "vídeo" | "documento" | "arquivo", para a frase ficar natural. */
function rotuloDoTipo(tipo: MensagemDto['tipo']): string {
  if (tipo === 'imagem') return 'Imagem'
  if (tipo === 'audio') return 'Áudio'
  if (tipo === 'video') return 'Vídeo'
  if (tipo === 'documento') return 'Documento'
  return 'Arquivo'
}

/** O que ficou escrito e não saiu, por conversa, enquanto a tela está aberta. */
const rascunhos = new Map<string, string>()

export function Conversa({
  conversa,
  chamado,
  usuario,
  aoEncerrar,
  aoTransferir,
  aoApoiar,
  aoAgendarRetorno,
  mensagemAlvo = null,
  aoSoltarAlvo,
  palavraBuscada = null,
  aoVoltar,
}: {
  conversa: ConversaDto
  chamado: ChamadoDetalheDto | undefined
  usuario: UsuarioSessao
  aoEncerrar: () => void
  aoTransferir: () => void
  aoApoiar: () => void
  aoAgendarRetorno: () => void
  /** Veio de "Buscar em mensagens": a conversa abre nesta mensagem. */
  mensagemAlvo?: string | null
  /** Avisa que o destaque já cumpriu o papel e pode ser esquecido. */
  aoSoltarAlvo?: () => void
  /** A palavra de "Buscar em mensagens", destacada no texto e percorrida pelas setas. */
  palavraBuscada?: string | null
  /** Volta à lista. Só aparece no celular, onde a lista e a conversa não cabem lado a lado. */
  aoVoltar?: () => void
}) {
  const { data: ocorrencias } = useOcorrencias(conversa.chamadoId, palavraBuscada)

  /*
    Qual das vezes está em foco.

    Nasce na que a lista achou (a mais recente) e anda com as setas. Fica pelo
    índice, e não pelo id, porque é o índice que o contador "3 de 5" mostra e é
    dele que a navegação precisa para saber quando chegou à ponta.
  */
  const [indice, setIndice] = useState<number | null>(null)
  const lista = ocorrencias ?? []

  useEffect(() => {
    if (lista.length === 0) return setIndice(null)
    const daLista = lista.findIndex((o) => o.mensagemId === mensagemAlvo)
    setIndice(daLista >= 0 ? daLista : lista.length - 1)
  }, [ocorrencias, conversa.chamadoId])

  const emFoco = indice !== null ? (lista[indice]?.mensagemId ?? null) : null
  const alvo = emFoco ?? mensagemAlvo

  const { data: mensagens, isLoading } = useMensagens(conversa.chamadoId, alvo)
  const enviar = useEnviarMensagem()
  const enviarMidia = useEnviarMidia()
  const removerApoio = useRemoverApoio()
  const retomei = useRetomei()
  // Abriu a conversa que voltou do apoio: ela sai do bloco "Saiu do apoio" (ADR 0043).
  const voltouDoApoio = Boolean(chamado?.saiuDoApoio && chamado.responsavelId === usuario.id)
  useEffect(() => {
    if (voltouDoApoio) retomei.mutate(conversa.chamadoId)
    // Uma vez por conversa aberta; a mutação muda a cada render.
  }, [voltouDoApoio, conversa.chamadoId])

  /*
    O rascunho é de cada conversa.

    Com um campo só para todas, o que se escrevia para a Maria continuava na caixa ao abrir a
    conversa do João, pronto para sair para ele no Enter. Agora cada conversa guarda o seu, e
    voltar à da Maria devolve o que tinha ficado lá.
  */
  const [texto, setTexto] = useState(() => rascunhos.get(conversa.chamadoId) ?? '')
  const [textoDe, setTextoDe] = useState(conversa.chamadoId)
  if (textoDe !== conversa.chamadoId) {
    setTextoDe(conversa.chamadoId)
    setTexto(rascunhos.get(conversa.chamadoId) ?? '')
  }
  useEffect(() => {
    if (texto) rascunhos.set(conversa.chamadoId, texto)
    else rascunhos.delete(conversa.chamadoId)
  }, [texto, conversa.chamadoId])
  const [nota, setNota] = useState(false)
  const [ampliada, setAmpliada] = useState<MidiaParaVer | null>(null)
  const [fichaAberta, setFichaAberta] = useState(false)
  const rolagem = useRef<HTMLDivElement>(null)

  // Ações da setinha de cada mensagem (ADR 0026).
  const apagarMensagem = useApagarMensagem()
  const reabrir = useReabrirChamado()
  const { pedirConfirmacao, dialogo } = usePerguntar()
  /** A mensagem que a próxima resposta cita, mostrada em cima da caixa de escrever. */
  const [respondendo, setRespondendo] = useState<CitacaoDoEnvio | null>(null)
  const [editando, setEditando] = useState<MensagemDto | null>(null)
  /**
   * A cópia que acabou de acontecer, para o rodapé do balão confirmar por um
   * instante. Quando o navegador recusa, diz isso também: sem aviso, a pessoa
   * colaria o que estava antes na área de transferência achando que era a
   * mensagem.
   */
  const [copia, setCopia] = useState<{ mensagemId: string; ok: boolean } | null>(null)
  /** A citada para onde a pessoa acabou de pular, destacada por um instante. */
  const [realcadaId, setRealcadaId] = useState<string | null>(null)

  // Trocar de conversa leva junto a resposta e a edição: citar a mensagem de
  // um cliente na conversa de outro seria o pior engano possível aqui.
  useEffect(() => {
    setRespondendo(null)
    setEditando(null)
  }, [conversa.chamadoId])

  /*
    Se a conversa está parada longe do fim.

    Vale a pena saber porque mensagem nova chega por baixo: quem subiu para
    reler algo não é arrastado junto, e sem um caminho de volta acaba rolando
    no dedo até o fim — em conversa de meses, isso é muita rolagem.
  */
  const [longeDoFim, setLongeDoFim] = useState(false)

  /**
   * Leva a conversa até a última mensagem.
   *
   * Rola a caixa das mensagens, e não um elemento marcador dentro dela:
   * `scrollIntoView` sobe a árvore procurando quem rola, e basta um ancestral
   * com `overflow` fechado para a versão suave ser descartada em silêncio.
   * Aqui o alvo é explícito.
   *
   * O `behavior: 'smooth'` também não é garantido: navegador embarcado e
   * sistema com animações desligadas simplesmente ignoram o pedido, e a
   * conversa fica parada onde estava. Por isso a conferência logo depois — se
   * não saiu do lugar, vai de uma vez. É o que faz a conversa abrir na última
   * mensagem mesmo nessas máquinas.
   */
  function irParaOFim() {
    const caixa = rolagem.current
    if (!caixa) return

    const destino = caixa.scrollHeight
    const partiuDe = caixa.scrollTop
    caixa.scrollTo({ top: destino, behavior: 'smooth' })

    window.setTimeout(() => {
      const atual = rolagem.current
      if (atual && atual.scrollTop === partiuDe && partiuDe !== destino) {
        atual.scrollTop = atual.scrollHeight
      }
    }, 120)
  }
  const campoTexto = useRef<HTMLTextAreaElement>(null)
  const entradaMidia = useRef<HTMLInputElement>(null)
  const entradaArquivo = useRef<HTMLInputElement>(null)

  function escolherAnexo(tipo: 'midia' | 'arquivo') {
    const alvo = tipo === 'midia' ? entradaMidia : entradaArquivo
    alvo.current?.click()
  }

  function receberArquivo(evento: React.ChangeEvent<HTMLInputElement>) {
    const arquivo = evento.target.files?.[0]
    if (arquivo) enviarMidia.mutate({ chamadoId: conversa.chamadoId, arquivo })
    // Zera sempre: sem isto, escolher o mesmo arquivo duas vezes seguidas não
    // dispara `change` na segunda, e parece que o clique não funcionou.
    evento.target.value = ''
  }

  /**
   * Emoji entra onde está o cursor, não no fim do texto.
   *
   * Quem escreve "obrigado pela preferência" e volta para pôr um sorriso
   * depois de "obrigado" espera que ele caia ali. Grudar tudo no fim obriga a
   * recortar e colar, que é pior do que não ter o botão.
   */
  function inserirEmoji(emoji: string) {
    const campo = campoTexto.current
    if (!campo) {
      setTexto((t) => t + emoji)
      return
    }

    const inicio = campo.selectionStart ?? campo.value.length
    const fimSelecao = campo.selectionEnd ?? inicio
    setTexto(campo.value.slice(0, inicio) + emoji + campo.value.slice(fimSelecao))

    // O cursor precisa ir para depois do emoji, e o React só repinta o valor
    // no quadro seguinte — daí o adiamento.
    requestAnimationFrame(() => {
      campo.focus()
      const posicao = inicio + emoji.length
      campo.setSelectionRange(posicao, posicao)
    })
  }

  /*
    Onde a conversa abre: no fim, ou na mensagem que a busca achou.

    Com alvo, rolar para o fim seria desfazer o que a lupa fez. O destaque
    fica por alguns segundos, tempo de o olho achar a mensagem no meio das
    outras, e some sozinho para não virar marca permanente na conversa.
  */
  useEffect(() => {
    if (!mensagens?.length) return

    if (alvo) {
      const balao = document.getElementById(`mensagem-${alvo}`)
      balao?.scrollIntoView({ block: 'center' })
      // Só o alvo que veio da lista se apaga sozinho; andando pelas setas, o
      // destaque acompanha a navegação e some quando a busca sai da tela.
      if (alvo === mensagemAlvo && lista.length <= 1) {
        const relogio = setTimeout(() => aoSoltarAlvo?.(), 4000)
        return () => clearTimeout(relogio)
      }
      return
    }

    irParaOFim()
  }, [mensagens, alvo])

  const erroBruto = enviar.error ?? enviarMidia.error ?? apagarMensagem.error ?? reabrir.error
  const erro = erroBruto instanceof ErroRequisicao ? erroBruto : null
  const fechado = chamado?.status === 'fechado'
  const somenteLeitura = chamado?.somenteLeitura ?? false

  /*
    Editar e apagar mandam para o WhatsApp, então pedem um número que envia e
    a permissão de enviar. Responder pede também a conversa aberta, porque a
    resposta sai pela caixa de escrever, que some na conversa encerrada.
  */
  const podeTexto = pode(usuario, 'bate_papo.criar')
  const podeResponder = !somenteLeitura && podeTexto && !fechado

  /*
    Cada coisa da caixa de escrever tem a sua permissão (ADR 0038): mandar
    texto, mandar arquivo, escrever nota interna, editar e apagar o que já foi.
    Quem só pode anotar escreve sempre em modo comentário.
  */
  const podeArquivos = pode(usuario, 'bate_papo.arquivos')
  const podeNota = pode(usuario, 'bate_papo.nota_interna')
  const podeEditarMensagem = !somenteLeitura && pode(usuario, 'bate_papo.editar_mensagem')
  const podeApagarMensagem = !somenteLeitura && pode(usuario, 'bate_papo.apagar_mensagem')
  const soAnota = !podeTexto && podeNota

  /*
    Conversa de outra pessoa (mapa de uso, 08/10/2026): "a área de escrita
    informa que o cliente é de outra pessoa, e embaixo o botão comentário e
    abaixo dele transferir". Antes, quem via a aba Todas respondia o cliente do
    colega sem aviso nenhum. Vale para todos, o proprietário também. Quem está
    de apoio aceito escreve normalmente; a fila (sem dono) também.
  */
  const deOutraPessoa = Boolean(
    chamado?.responsavelId &&
    chamado.responsavelId !== usuario.id &&
    !chamado.apoios.some((a) => a.usuarioId === usuario.id),
  )
  useEffect(() => {
    if (soAnota) setNota(true)
    else if (!podeNota) setNota(false)
  }, [soAnota, podeNota])

  /*
    API oficial (ADR 0033): fora da janela de 24 horas, só modelo aprovado.
    A janela é refeita a cada minuto na tela, porque ela fecha sozinha com o
    tempo, sem mensagem nova que atualize a conversa.
  */
  const provedor = chamado?.provedor ?? 'evolution'
  const oficial = provedor === 'oficial'
  const agora = useAgoraDoMinuto(oficial)
  const fimDaJanela = chamado?.janela?.fechaEm ? new Date(chamado.janela.fechaEm) : null
  const janelaAberta = !oficial || (fimDaJanela !== null && fimDaJanela.getTime() > agora)
  const travadoPelaJanela = !janelaAberta
  const [escolhendoModelo, setEscolhendoModelo] = useState(false)

  /**
   * Reabrir traz a conversa para quem reabriu, em "Minhas". Mora aqui, e não
   * na página, para o erro aparecer no rodapé: antes, "já existe outra
   * conversa aberta deste contato" era engolido e o botão parecia não fazer
   * nada.
   */
  const podeReabrir = pode(usuario, 'bate_papo.editar')
  const reabrirConversa = () => reabrir.mutate(conversa.chamadoId)

  async function agirNaMensagem(acao: AcaoDaMensagem, mensagem: MensagemDto) {
    if (acao === 'responder') {
      setRespondendo({
        mensagemId: mensagem.id,
        trecho: trechoParaCitar(mensagem.conteudo, mensagem.tipo),
        daEmpresa: mensagem.direcao === 'saida',
        quem: mensagem.direcao === 'saida' ? (mensagem.autorNome ?? 'Equipe') : conversa.nome,
      })
      // Resposta vai para o cliente: sai do modo comentário, se estava nele.
      setNota(false)
      campoTexto.current?.focus()
      return
    }

    if (acao === 'copiar') {
      // A assinatura fica de fora: quem copia quer o que foi dito.
      const texto =
        mensagem.direcao === 'saida'
          ? semAssinatura(mensagem.conteudo ?? '')
          : (mensagem.conteudo ?? '')
      const ok = await copiarTexto(texto)
      setCopia({ mensagemId: mensagem.id, ok })
      window.setTimeout(
        () => setCopia((c) => (c?.mensagemId === mensagem.id ? null : c)),
        ok ? 2000 : 4000,
      )
      return
    }

    if (acao === 'editar') {
      setEditando(mensagem)
      return
    }

    const confirmado = await pedirConfirmacao(
      'O cliente deixa de ver esta mensagem no WhatsApp dele, e no lugar aparece "Mensagem apagada". Aqui no CRM ela continua na conversa, marcada como apagada, com o texto guardado para consulta.',
      { titulo: 'Apagar para todos', rotuloConfirmar: 'Apagar para todos' },
    )
    if (confirmado) {
      apagarMensagem.mutate({ chamadoId: conversa.chamadoId, mensagemId: mensagem.id })
    }
  }

  /** Pula até a mensagem citada, quando ela está entre as carregadas. */
  function irParaCitada(mensagemId: string | null) {
    if (!mensagemId) return
    const balao = document.getElementById(`mensagem-${mensagemId}`)
    if (!balao) return
    balao.scrollIntoView({ block: 'center' })
    setRealcadaId(mensagemId)
    window.setTimeout(() => setRealcadaId((r) => (r === mensagemId ? null : r)), 2000)
  }

  function mandar() {
    if (!texto.trim()) return
    // Janela fechada: o texto livre não sai. Comentário da equipe sim.
    if (travadoPelaJanela && !nota) return

    /*
      Mensagem ao cliente espera 5 segundos antes de sair; comentário não.

      O comentário fica só aqui dentro e se corrige editando, então não há o
      que desfazer. A mensagem ao cliente vai para a fila, e o campo esvazia
      na hora — para quem escreve, ela já foi, com a chance de voltar atrás.
    */
    if (nota) {
      enviar.mutate(
        { chamadoId: conversa.chamadoId, conteudo: texto, notaInterna: true },
        {
          onSuccess: () => {
            setTexto('')
            // Depois do comentário a caixa volta para o cliente (mapa de uso, 08/10/2026).
            if (!soAnota) setNota(false)
          },
        },
      )
      return
    }

    agendarEnvio(conversa.chamadoId, texto, respondendo)
    setTexto('')
    setRespondendo(null)
    campoTexto.current?.focus()
  }

  /**
   * O texto cancelado volta para o campo.
   *
   * Se a pessoa já começou outra mensagem, o cancelado entra antes dela em vez
   * de apagá-la: cancelar não pode custar o que foi escrito depois.
   */
  function devolverTexto(cancelado: string, citacao: CitacaoDoEnvio | null) {
    // A citação volta junto, a menos que a pessoa já esteja respondendo outra.
    if (citacao) setRespondendo((atual) => atual ?? citacao)
    setTexto((atual) =>
      atual.trim()
        ? `${cancelado}
${atual}`
        : cancelado,
    )
    campoTexto.current?.focus()
  }

  return (
    /*
      Duas colunas: a conversa e a ficha. A ficha só ocupa espaço quando está
      aberta, e o chat encolhe em vez de ser coberto — quem consulta o
      protocolo continua vendo o que o cliente acabou de escrever.
    */
    <div className="flex h-full min-w-0">
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="@container flex items-center gap-2 border-b border-tinta-200 bg-superficie px-2 py-2.5 sm:gap-3 sm:px-4">
          {aoVoltar ? (
            <button
              type="button"
              onClick={aoVoltar}
              aria-label="Voltar às conversas"
              title="Voltar às conversas"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-tinta-600 hover:bg-tinta-50 hover:text-tinta-900 md:hidden"
            >
              <IconeSetaBaixo className="h-5 w-5 rotate-90" />
            </button>
          ) : null}
          {/*
          Nome e foto abrem a ficha do contato.

          É o gesto que a equipe já traz do Digisac: para saber com quem se
          está falando, clica-se no nome. Antes o dado do contato só existia
          espremido no subtítulo, e o que não coubesse ali não existia.
        */}
          {/*
            Um bloco, e não um botão: as etapas do funil, na linha do
            telefone, são botões próprios, e botão dentro de botão não vale.
            O clique em qualquer outro ponto do bloco abre a ficha; o nome é
            o botão que o teclado alcança.
          */}
          <div
            onClick={() => setFichaAberta(true)}
            className="flex min-w-0 cursor-pointer items-center gap-3 rounded-xl px-1.5 py-0.5 text-left hover:bg-tinta-50"
            title="Abrir a ficha do contato"
          >
            <Avatar contatoId={conversa.contatoId} nome={conversa.nome} />

            <div className="min-w-0">
              {/*
                As etiquetas moram na linha do nome, e não soltas no cabeçalho.

                Soltas, centralizavam na altura do bloco de duas linhas (nome e
                telefone) e ficavam flutuando entre uma e outra, sem pertencer a
                nenhuma. Na linha do nome, alinham com ele.

                Respondem à largura do cabeçalho, e não da janela: a mesma
                janela deixa espaços diferentes com a ficha aberta ou fechada.
                48rem (768px) é onde cabem um nome legível, as três etiquetas e
                os ícones de ação. Abaixo disso cedem as etiquetas, que recolhem
                no botão de informação ao lado: são contexto, os ícones são o
                que se clica.
              */}
              <p className="flex min-w-0 items-center gap-2 font-medium text-tinta-900">
                <button type="button" className="min-w-0 text-left">
                  <NomeDoContato nome={conversa.nome} nomeOriginal={conversa.nomeOriginal} />
                </button>
                <span className="hidden shrink-0 items-center gap-1 font-normal @3xl:flex">
                  <EtiquetasDoAtendimento chamado={chamado} />
                </span>
              </p>
              <p className="flex min-w-0 items-center gap-1.5 truncate text-xs text-tinta-400">
                {/*
                O funil aparece quando existe, e só.

                Antes, não estando em funil nenhum, o subtítulo dizia "fora do
                CRM" — vocabulário de venda, cobrando uma providência. Mas o
                sistema atende venda, suporte, financeiro e o que mais for
                criado: no suporte o contato **não deve** estar num funil, e a
                etiqueta acusava um problema que não existe, em toda conversa,
                para sempre.
              */}
                <span className="shrink-0">
                  {conversa.telefone ?? 'sem número'}
                  {conversa.telefoneMascarado ? ' (mascarado)' : ''}
                </span>

                {/*
                  As etapas do funil em quadradinhos (06/10/2026), no lugar do
                  texto "Funil / Etapa": a etapa continua escrita ao lado.
                */}
                {pode(usuario, 'leads.ver') ? (
                  <EtapasDoFunil conversa={conversa} usuario={usuario} />
                ) : conversa.funilNome ? (
                  <span className="truncate">
                    · {conversa.funilNome}
                    {conversa.etapaNome ? ` / ${conversa.etapaNome}` : ''}
                  </span>
                ) : null}

                {/*
                "Cliente novo" some sozinho depois de 72h, e por isso não pode
                ser texto no meio da frase: é o único aviso ali com prazo, e
                quem bate o olho precisa distinguir "esta pessoa apareceu
                agora" de "esta pessoa não está em funil nenhum" — que é o que
                "fora do CRM" diz, e vale para cliente de dois anos também.
              */}
                {conversa.clienteNovo ? (
                  <span
                    className="shrink-0 rounded bg-green-100 px-1.5 py-0.5 text-[11px] font-medium text-green-800"
                    title="Falou com a empresa pela primeira vez nas últimas 72 horas"
                  >
                    cliente novo
                  </span>
                ) : null}
              </p>
            </div>
          </div>

          {/* Cabeçalho estreito: as etiquetas recolhidas num botão. */}
          <div className="shrink-0 @3xl:hidden">
            <InfoDoAtendimento chamado={chamado} />
          </div>

          {/*
          Ações à direita, só com ícone. Com o texto eram oito botões de
          ~120px que quebravam em linhas e empurravam as mensagens para baixo;
          como ícone cabem numa fileira ao lado do nome, e o nome de cada uma
          aparece no hover.

          `min-w-0` no lugar do `shrink-0` de antes: a barra precisa poder
          encolher para saber que encolheu — é a medida dela que decide o que
          vai para o menu. Com `shrink-0` ela mantinha a largura de oito ícones
          e simplesmente vazava para fora da tela.

          `min-w-[34px]`, e não zero: é a largura de um botão. Com zero, a barra
          encolhia até sumir, mas o botão "⋯" continuava lá dentro, e como ela
          alinha pela direita, ele transbordava para a esquerda, por cima das
          etiquetas. Era a sobreposição que aparecia ao diminuir a janela.
        */}
          <div className="ml-auto flex min-w-[34px] flex-1 items-center gap-0.5">
            <BarraDeAcoes
              conversa={conversa}
              chamado={chamado}
              usuario={usuario}
              aoEncerrar={aoEncerrar}
              aoTransferir={aoTransferir}
              aoApoiar={aoApoiar}
              aoAgendarRetorno={aoAgendarRetorno}
              aoReabrir={podeReabrir ? reabrirConversa : undefined}
            />
          </div>
        </header>

        {/* [INÍCIO] Resumo Gemini: faixa curta (só a primeira linha); o texto completo está na ficha */}
        {chamado?.resumoIa ? (
          <div className="border-b border-tinta-100 bg-superficie px-4 py-2 text-sm text-tinta-700">
            <p className="leading-snug">{chamado.resumoIa.split('\n')[0]}</p>
            <p className="mt-1 text-xs text-tinta-500">
              {chamado.matriculaIa === 'sim'
                ? 'Indica matrícula'
                : chamado.matriculaIa === 'nao'
                  ? 'Não indica matrícula'
                  : 'Matrícula ainda indefinida'}
            </p>
          </div>
        ) : null}
        {/* [FIM] Resumo Gemini */}

        {/* O pedido de apoio feito a quem está olhando: responde aqui (ADR 0043). */}
        {chamado?.pedidoDeApoioParaMim ? (
          <FaixaDoPedido chamadoId={conversa.chamadoId} pedido={chamado.pedidoDeApoioParaMim} />
        ) : null}

        {/* Barra de lembrete enquanto houver apoio ativo ou pedido sem resposta (4.6, ADR 0043). */}
        {chamado &&
        !chamado.pedidoDeApoioParaMim &&
        (chamado.apoios.length > 0 || chamado.pedidosDeApoio.length > 0) ? (
          <div
            className="flex flex-wrap items-center justify-between gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900"
          >
            <span>
              {chamado.responsavelId !== usuario.id &&
              chamado.apoios.some((a) => a.usuarioId === usuario.id)
                ? `Você está de apoio nesta conversa${
                    chamado.responsavelNome ? `, que é de ${chamado.responsavelNome}` : ''
                  }. Quando terminar, use Fechar para mim, no alto.`
                : [
                    chamado.apoios.length > 0
                      ? `Apoio ativo: ${chamado.apoios.map((a) => a.nome).join(', ')}.`
                      : null,
                    chamado.pedidosDeApoio.length > 0
                      ? `Pedido de apoio para ${chamado.pedidosDeApoio
                          .map((a) => a.nome)
                          .join(', ')}, esperando resposta.`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' ')}
            </span>
            {chamado.responsavelId === usuario.id || pode(usuario, 'bate_papo.tirar_apoio') ? (
              <div className="flex flex-wrap gap-1">
                {/* O próprio apoio sai pelo "Fechar para mim", no alto: aqui só os dos outros. */}
                {chamado.apoios
                  .filter((apoio) => apoio.usuarioId !== usuario.id)
                  .map((apoio) => (
                    <Botao
                      key={apoio.id}
                      variante="texto"
                      onClick={() =>
                        removerApoio.mutate({ chamadoId: conversa.chamadoId, apoioId: apoio.id })
                      }
                    >
                      Encerrar apoio de {apoio.nome}
                    </Botao>
                  ))}
                {chamado.pedidosDeApoio.map((pedido) => (
                  <Botao
                    key={pedido.id}
                    variante="texto"
                    onClick={() =>
                      removerApoio.mutate({ chamadoId: conversa.chamadoId, apoioId: pedido.id })
                    }
                  >
                    Cancelar pedido a {pedido.nome}
                  </Botao>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {somenteLeitura ? (
          <div className="border-b border-tinta-200 bg-tinta-100 px-4 py-2 text-sm text-tinta-600">
            Este número <strong>só recebe</strong>: a mensagem do cliente chega e fica registrada
            aqui, mas o envio está desligado, para o CRM e o Digisac não responderem a mesma pessoa.
            Quem libera é a Configuração, em Números.
          </div>
        ) : null}

        {chamado?.anuncio ? <FaixaDeAnuncio chamado={chamado} usuario={usuario} /> : null}

        {/*
        O fundo da conversa é mais escuro que o balão recebido, não igual.

        Com os dois em branco, a mensagem do cliente perdia a forma e virava
        texto solto sobre a página — some justamente o recurso que faz uma
        conversa parecer uma conversa. O degrau é pequeno de propósito: o que
        precisa saltar é o balão, não o fundo.
      */}
        {palavraBuscada && indice !== null ? (
          <NavegarNaBusca
            palavra={palavraBuscada}
            ocorrencias={lista}
            indice={indice}
            aoTrocar={setIndice}
          />
        ) : null}

        {/*
          `relative`: o botão de descer se posiciona contra esta caixa.

          `min-h-0` e não `overflow-hidden`: quem segura a altura dentro do
          flex é o `min-h-0`, e quem corta é o filho, que já rola. Com
          `overflow-hidden` aqui, o Chromium cancela em silêncio qualquer
          rolagem suave pedida lá dentro — inclusive a que abre a conversa na
          última mensagem.
        */}
        <div className="relative flex min-h-0 flex-1 flex-col">
          <div
            ref={rolagem}
            onScroll={(e) => {
              const alvoDaRolagem = e.currentTarget
              const sobrando =
                alvoDaRolagem.scrollHeight - alvoDaRolagem.scrollTop - alvoDaRolagem.clientHeight
              // Uma tela inteira de folga: menos que isso, o fim ainda está à
              // vista e o botão seria só um enfeite por cima da conversa.
              setLongeDoFim(sobrando > alvoDaRolagem.clientHeight)
            }}
            className="flex-1 space-y-2.5 overflow-y-auto bg-tinta-100 p-4"
          >
            {isLoading ? (
              <Carregando texto="Carregando a conversa…" />
            ) : (mensagens ?? []).length === 0 ? (
              // Conversa aberta pelo "+" nasce vazia; o fundo liso parecia carregamento travado.
              <p className="mx-auto mt-8 max-w-xs text-center text-sm text-tinta-400">
                Nenhuma mensagem ainda. O cliente só recebe alguma coisa quando você enviar a
                primeira.
              </p>
            ) : (
              (mensagens ?? []).map((mensagem) => (
                <Balao
                  key={mensagem.id}
                  mensagem={mensagem}
                  aoAmpliar={setAmpliada}
                  destacada={mensagem.id === alvo || mensagem.id === realcadaId}
                  realce={palavraBuscada}
                  opcoes={opcoesDaMensagem(mensagem, usuario.id, {
                    podeResponder,
                    podeEditar: podeEditarMensagem,
                    podeApagar: podeApagarMensagem,
                    provedor,
                  })}
                  aoAgir={(acao) => void agirNaMensagem(acao, mensagem)}
                  copia={copia?.mensagemId === mensagem.id ? copia.ok : null}
                  nomeDoContato={conversa.nome}
                  aoIrParaCitada={irParaCitada}
                />
              ))
            )}
          </div>

          {/*
            O atalho de volta ao fim, como no WhatsApp.

            Só aparece quando some do fim de vista: um botão fixo sobre a
            conversa tampa mensagem o tempo todo para resolver um problema que
            só existe de vez em quando.
          */}
          {longeDoFim ? (
            <button
              type="button"
              aria-label="Ir para a última mensagem"
              title="Ir para a última mensagem"
              onClick={() => irParaOFim()}
              className="absolute bottom-4 left-1/2 flex h-9 w-9 -translate-x-1/2 items-center justify-center rounded-full bg-superficie text-tinta-600 shadow-media ring-1 ring-tinta-200 transition hover:bg-tinta-50 hover:text-tinta-900"
            >
              <IconeSetaBaixo className="h-5 w-5" />
            </button>
          ) : null}
        </div>

        <EnviosPendentes chamadoId={conversa.chamadoId} aoDevolverTexto={devolverTexto} />

        {/* `relative`: é contra esta caixa que o painel de respostas se posiciona. */}
        <footer
          className="relative border-t border-tinta-200 bg-superficie p-3"
        >
          {erro ? (
            <div className="mb-2">
              <Aviso tipo="erro">{erro.corpo.mensagem}</Aviso>
            </div>
          ) : null}

          {fechado ? (
            /*
              O botão fica no próprio aviso: é ali que a pessoa lê que a
              conversa acabou, e procurar o "reabrir" no menu do cabeçalho era
              um passo que ninguém adivinhava.
            */
            <div className="flex flex-wrap items-center gap-3">
              <div className="min-w-0 flex-1">
                <Aviso>
                  Esta conversa está encerrada.
                  {podeReabrir
                    ? ' Ao reabrir, ela vem para as suas conversas, em Minhas.'
                    : ' Peça a quem pode reabrir para voltar a falar com o cliente.'}
                </Aviso>
              </div>
              {podeReabrir ? (
                <Botao variante="primario" onClick={reabrirConversa} carregando={reabrir.isPending}>
                  <IconeReabrir className="h-4 w-4" />
                  Reabrir
                </Botao>
              ) : null}
            </div>
          ) : !podeTexto && !podeNota ? (
            <Aviso>Você pode ler esta conversa, mas não mandar mensagem nem anotar.</Aviso>
          ) : deOutraPessoa && !nota ? (
            <div className="space-y-2">
              <Aviso>
                Este cliente é de {chamado?.responsavelNome ?? 'outra pessoa'}. Você pode deixar um
                comentário para a equipe ou transferir a conversa.
              </Aviso>
              <div className="flex flex-col items-start gap-2">
                {podeNota ? (
                  <Botao
                    onClick={() => {
                      setNota(true)
                      window.setTimeout(() => campoTexto.current?.focus(), 0)
                    }}
                  >
                    <IconeNotaInterna className="h-4 w-4" />
                    Comentário
                  </Botao>
                ) : null}
                {pode(usuario, 'transferir_chamado') ? (
                  <Botao onClick={aoTransferir}>
                    <IconeTransferir className="h-4 w-4" />
                    Transferir
                  </Botao>
                ) : null}
              </div>
            </div>
          ) : (
            <>
              {oficial ? (
                <FaixaDaJanela
                  aberta={janelaAberta}
                  fechaEm={fimDaJanela}
                  agora={agora}
                  podeEnviar={podeResponder}
                  aoEscolherModelo={() => setEscolhendoModelo(true)}
                />
              ) : null}
              {escolhendoModelo ? (
                <JanelaDeModelos
                  chamadoId={conversa.chamadoId}
                  janelaAberta={janelaAberta}
                  aoFechar={() => setEscolhendoModelo(false)}
                />
              ) : null}
              {/*
              Campo e barra de ferramentas dentro da mesma moldura.

              Antes eram três blocos empilhados — caixa de comentário, campo,
              dois avisos de rodapé — e o rodapé sozinho comia um quarto da
              altura do chat. Os avisos saíram: o de assinatura repetia em toda
              tela algo que se aprende na primeira mensagem, e o de tamanho de
              arquivo só interessa na hora de anexar, onde o próprio erro
              explica. A moldura inteira muda de cor no comentário, que é um
              sinal mais difícil de ignorar do que a caixinha marcada.
            */}
              <div
                className={juntar(
                  'rounded-2xl border transition-colors focus-within:ring-2 focus-within:ring-marca/25',
                  nota ? 'border-amber-300 bg-amber-50' : 'border-tinta-200 bg-superficie',
                )}
              >
                {/*
                  A mensagem que esta resposta cita, como no WhatsApp: em cima
                  do campo, com o X para desistir. Some no comentário, que é
                  interno e não cita nada no WhatsApp do cliente.
                */}
                {respondendo && !nota ? (
                  <div className="flex items-start gap-2 px-2 pt-2">
                    <div className="min-w-0 flex-1 rounded-md border-l-4 border-marca bg-tinta-50 px-2 py-1">
                      <p className="text-xs font-semibold text-marca">
                        Respondendo a {respondendo.quem}
                      </p>
                      <p className="truncate text-xs text-tinta-600">
                        <TextoWhatsApp texto={respondendo.trecho} />
                      </p>
                    </div>
                    <BotaoIcone
                      rotulo="Cancelar a resposta"
                      pequeno
                      onClick={() => setRespondendo(null)}
                    >
                      <IconeX className="h-3.5 w-3.5" />
                    </BotaoIcone>
                  </div>
                ) : null}

                <textarea
                  ref={campoTexto}
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      mandar()
                    }
                    if (e.key === 'Escape' && respondendo) setRespondendo(null)
                  }}
                  rows={2}
                  placeholder={
                    nota
                      ? 'Comentário para a equipe…'
                      : travadoPelaJanela
                        ? 'Janela fechada: mande um modelo aprovado, ou escreva um comentário para a equipe.'
                        : 'Escreva a mensagem…'
                  }
                  className="w-full resize-none bg-transparent px-3 pt-2 text-sm outline-none"
                />

                <div className="flex items-center gap-0.5 px-1.5 pb-1.5">
                  {podeArquivos ? (
                    <span className="inline-flex">
                      <SeletorAnexo
                        aoEscolher={escolherAnexo}
                        desabilitado={
                          nota || somenteLeitura || travadoPelaJanela || enviarMidia.isPending
                        }
                      />
                    </span>
                  ) : null}

                  {/*
                  A biblioteca fica ao lado do clipe porque resolve a mesma
                  frase — "manda a foto da escola" —, só que com o arquivo
                  que a empresa já tem pronto, em vez de um do computador.
                */}
                  {podeArquivos ? (
                    <span className="inline-flex">
                      <BotaoMateriais
                        chamadoId={conversa.chamadoId}
                        usuario={usuario}
                        desabilitado={nota || somenteLeitura || travadoPelaJanela}
                      />
                    </span>
                  ) : null}

                  {/* Só no número da API oficial: é o único envio com a janela fechada. */}
                  {oficial ? (
                    <BotaoIcone
                      rotulo="Modelo aprovado"
                      onClick={() => setEscolhendoModelo(true)}
                      disabled={nota || !podeResponder}
                    >
                      <IconeModeloAprovado className="h-5 w-5" />
                    </BotaoIcone>
                  ) : null}

                  <span className="inline-flex">
                    <SeletorEmoji aoEscolher={inserirEmoji} />
                  </span>

                  <span className="inline-flex">
                    <SeletorRespostas
                      filtro={texto.startsWith('/') ? texto.slice(1) : undefined}
                      aoEscolher={(conteudo) => setTexto(conteudo)}
                    />
                  </span>

                  {/*
                  O comentário virou botão que fica aceso. O rótulo escrito
                  saiu do rodapé, mas não sumiu: está na dica do hover e no
                  `aria-label`, e o campo inteiro fica âmbar quando está ligado.
                */}
                  {/* O botão só faz sentido para quem pode as duas coisas. */}
                  {podeNota && podeTexto ? (
                    <BotaoIcone
                      rotulo="Comentário"
                      onClick={() => setNota((n) => !n)}
                      aria-pressed={nota}
                      className={nota ? 'bg-amber-200 text-amber-900' : undefined}
                    >
                      <IconeNotaInterna className="h-5 w-5" />
                    </BotaoIcone>
                  ) : null}

                  {/*
                    Conversa com apoio (pedido de 05/10/2026): o ícone sinaliza, e
                    o clique leva até o comentário deixado no pedido. Sem
                    comentário, fica só o sinal.
                  */}
                  {chamado && chamado.apoios.length > 0 ? (
                    <BotaoIcone
                      rotulo={`Conversa de apoio: ${chamado.apoios
                        .map((a) => a.nome)
                        .join(', ')} ajudando ${chamado.responsavelNome ?? 'o responsável'}${
                        chamado.mensagemDoPedidoId ? '. Clique para ver o pedido' : ''
                      }`}
                      onClick={() => irParaCitada(chamado.mensagemDoPedidoId)}
                      className="text-marca"
                    >
                      <IconeApoio className="h-5 w-5" />
                    </BotaoIcone>
                  ) : null}

                  <div className="ml-auto flex items-center gap-1">
                    {/*
                    Microfone à direita, colado no enviar — a posição do
                    WhatsApp e do Digisac. Some no comentário: anotação da
                    equipe é texto, não áudio para o cliente.
                  */}
                    {!nota && podeArquivos ? (
                      <span className="inline-flex">
                        <GravadorDeAudio
                          desabilitado={
                            somenteLeitura || travadoPelaJanela || enviarMidia.isPending
                          }
                          aoGravar={(audio) =>
                            enviarMidia.mutate({ chamadoId: conversa.chamadoId, arquivo: audio })
                          }
                        />
                      </span>
                    ) : null}

                    <Botao
                      variante="primario"
                      onClick={mandar}
                      carregando={enviar.isPending}
                      disabled={!texto.trim() || ((somenteLeitura || travadoPelaJanela) && !nota)}
                    >
                      <IconeEnviar className="h-4 w-4" />
                      {nota ? 'Anotar' : 'Enviar'}
                    </Botao>
                  </div>
                </div>
              </div>

              {/*
              Dois campos de arquivo, e não um: o `accept` é o que faz o
              explorador do sistema já abrir filtrando. Um campo só teria de
              trocar de `accept` antes de abrir, e o navegador nem sempre
              acompanha a troca no mesmo clique.
            */}
              <input
                ref={entradaMidia}
                type="file"
                accept="image/*,video/*"
                className="hidden"
                onChange={receberArquivo}
              />
              <input
                ref={entradaArquivo}
                type="file"
                className="hidden"
                onChange={receberArquivo}
              />
            </>
          )}
        </footer>

        {ampliada ? (
          <VisualizadorDeMidia midia={ampliada} aoFechar={() => setAmpliada(null)} />
        ) : null}

        <EditarMensagem
          chamadoId={conversa.chamadoId}
          mensagem={editando}
          aoFechar={() => setEditando(null)}
        />
        {dialogo}
      </div>

      <FichaDoContato
        conversa={conversa}
        chamado={chamado}
        aberta={fichaAberta}
        aoFechar={() => setFichaAberta(false)}
        aoAbrirMidia={setAmpliada}
        usuario={usuario}
      />
    </div>
  )
}

function Balao({
  mensagem,
  aoAmpliar,
  destacada = false,
  realce = null,
  opcoes = [],
  aoAgir,
  copia = null,
  nomeDoContato,
  aoIrParaCitada,
}: {
  mensagem: MensagemDto
  aoAmpliar: (midia: MidiaParaVer) => void
  /** A mensagem que a busca achou, enquanto o destaque dura. */
  destacada?: boolean
  /** A palavra procurada, pintada dentro do texto. */
  realce?: string | null
  /** O que a setinha oferece nesta mensagem, para esta pessoa (ADR 0026). */
  opcoes?: OpcaoDoMenu[]
  aoAgir?: (acao: AcaoDaMensagem) => void
  /** Acabou de ser copiada (`true`) ou a cópia falhou (`false`): o rodapé avisa. */
  copia?: boolean | null
  nomeDoContato: string
  aoIrParaCitada?: (mensagemId: string | null) => void
}) {
  // Antes do retorno do aviso de sistema: gancho do React não pode ser pulado.
  const [verOriginal, setVerOriginal] = useState(false)
  const [verAnteriores, setVerAnteriores] = useState(false)

  if (mensagem.tipo === 'sistema') {
    return (
      <div id={`mensagem-${mensagem.id}`} className="my-2 text-center">
        <span className="inline-block rounded-full bg-tinta-200 px-3 py-1 text-xs text-tinta-600">
          {mensagem.conteudo}
        </span>
      </div>
    )
  }

  const daEmpresa = mensagem.direcao === 'saida'
  const ehNota = mensagem.tipo === 'nota_interna'
  const naoEntregue = mensagem.metadados?.falhaEnvio === true
  const apagada = Boolean(mensagem.apagadaEm)

  /*
    A cor do balão fica numa variável porque a setinha usa a mesma: ela
    aparece por cima do começo do texto e precisa cobri-lo, senão as letras
    atravessam o ícone.
  */
  const fundo = ehNota
    ? 'bg-amber-50'
    : naoEntregue
      ? 'bg-red-50'
      : daEmpresa
        ? 'bg-balao-saida'
        : 'bg-superficie'

  const conteudo = (
    <>
      {mensagem.midiaSituacao === 'baixando' ? (
        <p className="text-xs italic opacity-70">baixando o arquivo…</p>
      ) : null}

      {/*
        Arquivo que o WhatsApp já apagou. Dizer isso é melhor do que um
        "baixando" eterno: a pessoa para de esperar e sabe que, se precisar
        mesmo, tem de procurar no celular.
      */}
      {mensagem.midiaSituacao === 'indisponivel' ? (
        <p className="text-xs italic opacity-70">
          {rotuloDoTipo(mensagem.tipo)} que o WhatsApp não guarda mais. Veio do histórico, e o
          arquivo ficou só no celular
        </p>
      ) : null}

      {mensagem.midiaUrl ? <Midia mensagem={mensagem} aoAmpliar={aoAmpliar} /> : null}

      {mensagem.conteudo ? (
        <p className="whitespace-pre-wrap break-words">
          <TextoWhatsApp texto={mensagem.conteudo} realce={realce} />
        </p>
      ) : null}
    </>
  )

  return (
    <div
      id={`mensagem-${mensagem.id}`}
      className={juntar(
        'flex scroll-my-6 rounded-xl transition-shadow',
        daEmpresa ? 'justify-end' : 'justify-start',
        // Anel âmbar, o mesmo tom que o sistema usa para "olhe aqui".
        destacada && 'ring-2 ring-amber-400 ring-offset-2 ring-offset-tinta-100',
      )}
    >
      <div
        className={juntar(
          /*
            O canto colado no lado de quem falou fica reto.

            É o detalhe que faz o balão "apontar" para o dono da fala sem
            precisar de setinha desenhada — e o que diferencia uma conversa de
            uma pilha de retângulos iguais.

            `group relative`: a setinha das opções mora no canto e aparece ao
            passar o mouse no balão inteiro, não só em cima dela.
          */
          'group relative max-w-[75%] px-3.5 py-2.5 text-sm shadow-suave',
          daEmpresa ? 'rounded-2xl rounded-br-md' : 'rounded-2xl rounded-bl-md',
          fundo,
          ehNota
            ? 'text-amber-900 ring-1 ring-amber-300'
            : naoEntregue
              ? 'border border-dashed border-red-300 text-red-900'
              : 'text-tinta-900',
        )}
      >
        {aoAgir ? <MenuDaMensagem opcoes={opcoes} aoEscolher={aoAgir} fundo={fundo} /> : null}

        {ehNota ? <p className="mb-1 text-[10px] font-semibold uppercase">Comentário</p> : null}

        {naoEntregue ? (
          <p className="mb-1 text-[10px] font-semibold uppercase">Não entregue ao cliente</p>
        ) : null}

        {mensagem.respondendoA && !apagada ? (
          <Citacao
            citada={mensagem.respondendoA}
            nomeDoContato={nomeDoContato}
            aoIr={aoIrParaCitada}
          />
        ) : null}

        {apagada ? (
          /*
            Apagada para todos: o cliente não vê mais, mas o registro do
            atendimento não perde o que foi dito. O texto fica recolhido, a um
            clique, para a conversa mostrar o que o cliente vê.
          */
          <>
            <p className="flex items-center gap-1.5 italic text-tinta-500">
              <IconeLixeira className="h-3.5 w-3.5 shrink-0" />
              Mensagem apagada para todos
            </p>
            <button
              type="button"
              onClick={() => setVerOriginal((v) => !v)}
              className="mt-1 text-xs font-medium text-marca hover:underline"
            >
              {verOriginal ? 'Esconder o que estava escrito' : 'Ver o que estava escrito'}
            </button>
            {verOriginal ? (
              <div className="mt-1.5 border-t border-black/10 pt-1.5 opacity-70">{conteudo}</div>
            ) : null}
          </>
        ) : (
          conteudo
        )}

        {/* O que estava escrito antes de cada edição, da mais antiga à mais nova. */}
        {verAnteriores && mensagem.editada && !apagada ? (
          <div className="mt-1.5 space-y-1.5 border-t border-black/10 pt-1.5 text-xs">
            {mensagem.editada.anteriores.map((versao) => (
              <div key={versao.ate} className="opacity-70">
                <p className="font-medium">Antes da edição de {formatarDataHora(versao.ate)}:</p>
                <p className="whitespace-pre-wrap break-words">
                  <TextoWhatsApp texto={semAssinatura(versao.conteudo)} />
                </p>
              </div>
            ))}
          </div>
        ) : null}

        <p
          className={juntar(
            'mt-1 text-right text-[10px]',
            // No balão verde a hora fica em verde-escuro: cinza sobre verde
            // reprova em contraste, e branco some.
            daEmpresa && !ehNota && !naoEntregue ? 'text-balao-saida-escuro/70' : 'text-tinta-400',
          )}
        >
          {mensagem.autorNome ? `${mensagem.autorNome} · ` : ''}
          {formatarDataHora(mensagem.enviadaEm)}
          {mensagem.editada && !apagada ? (
            <>
              {' · '}
              <button
                type="button"
                onClick={() => setVerAnteriores((v) => !v)}
                title="Ver o que estava escrito antes"
                className="underline decoration-dotted underline-offset-2 hover:opacity-80"
              >
                editada
              </button>
            </>
          ) : null}
          {apagada ? ` · apagada em ${formatarDataHora(mensagem.apagadaEm)}` : ''}
          {copia === true ? ' · copiada' : ''}
          {copia === false ? ' · não deu para copiar, selecione o texto e copie à mão' : ''}
        </p>
      </div>
    </div>
  )
}

/**
 * A mensagem citada, em cima da resposta, como o WhatsApp mostra.
 *
 * Clicar leva até ela quando está entre as carregadas. A barra da esquerda
 * tem a cor de quem falou: verde para a equipe, a cor da marca para o
 * cliente, para se ler de relance quem está sendo respondido.
 */
function Citacao({
  citada,
  nomeDoContato,
  aoIr,
}: {
  citada: MensagemCitadaDto
  nomeDoContato: string
  aoIr?: (mensagemId: string | null) => void
}) {
  const quem =
    citada.daEmpresa === true
      ? 'Equipe'
      : citada.daEmpresa === false
        ? nomeDoContato
        : 'Mensagem citada'

  return (
    <button
      type="button"
      onClick={() => aoIr?.(citada.mensagemId)}
      disabled={!citada.mensagemId}
      title={citada.mensagemId ? 'Ir até a mensagem citada' : undefined}
      className={juntar(
        'mb-1.5 block w-full min-w-0 rounded-md border-l-4 bg-black/5 px-2 py-1 text-left text-xs',
        citada.daEmpresa ? 'border-balao-saida-escuro/50' : 'border-marca',
        citada.mensagemId ? 'hover:bg-black/10' : 'cursor-default',
      )}
    >
      <span className="block font-semibold text-tinta-700">{quem}</span>
      <span className="line-clamp-2 whitespace-pre-wrap break-words text-tinta-600">
        {/* Com o negrito e o itálico do WhatsApp, como o texto aparece no balão. */}
        <TextoWhatsApp texto={citada.trecho} />
      </span>
    </button>
  )
}

function Midia({
  mensagem,
  aoAmpliar,
}: {
  mensagem: MensagemDto
  aoAmpliar: (midia: MidiaParaVer) => void
}) {
  const url = mensagem.midiaUrl as string
  const abrir = () => {
    const midia = midiaDaMensagem(mensagem)
    if (midia) aoAmpliar(midia)
  }

  if (mensagem.tipo === 'imagem') {
    return (
      /*
        `aria-label` no botão, e não `alt` na imagem.

        A miniatura era um botão sem nome nenhum: quem usa leitor de tela ouvia
        "botão" cinco vezes seguidas numa conversa com cinco fotos, sem saber o
        que cada um faz. O `alt` fica vazio de propósito — a imagem é
        decorativa dentro do botão, e descrevê-la duas vezes seria pior.
      */
      <button
        type="button"
        onClick={abrir}
        aria-label={mensagem.conteudo ? `Ampliar imagem: ${mensagem.conteudo}` : 'Ampliar imagem'}
        className="mb-1 block cursor-zoom-in"
      >
        <img src={url} alt="" className="max-h-64 rounded" />
      </button>
    )
  }

  /**
   * Largura fixa nos dois players, e não altura.
   *
   * O Chrome esconde controles quando o elemento é estreito: o volume some
   * primeiro. Vídeo de WhatsApp é quase sempre retrato (720x1280), então
   * limitar pela altura deixava o player com cerca de 144px de largura — o
   * suficiente para o Chrome mostrar só play e tempo, sem volume.
   *
   * Com largura fixa e `object-contain`, o retrato aparece com tarja ao lado
   * em vez de espremer o player. O fundo preto é o que faz a tarja parecer
   * intencional em vez de defeito.
   */
  if (mensagem.tipo === 'audio') {
    return <audio controls src={url} className="mb-1 w-80 max-w-full" />
  }
  /*
    Vídeo pesado comprimido em segundo plano (ADR 0034): uma linha discreta
    diz de quanto para quanto, para ninguém estranhar o arquivo mais leve.
  */
  const compressao = (mensagem.metadados?.compressao ?? null) as {
    de?: number
    para?: number
  } | null
  const notaDeCompressao =
    compressao?.de && compressao.para ? (
      <p className="mb-1 text-[11px] opacity-70">
        Vídeo comprimido: {tamanhoLegivel(compressao.de)} para {tamanhoLegivel(compressao.para)}
      </p>
    ) : null

  if (mensagem.tipo === 'video') {
    return (
      <>
        <PlayerDeVideo
          url={url}
          aoTelaCheiaRecusada={abrir}
          className="mb-1 h-72 w-80 max-w-full"
        />
        {notaDeCompressao}
      </>
    )
  }

  /*
    Vídeo que chegou como documento: depois de comprimido ele é MP4 comum,
    e toca aqui mesmo em vez de pedir para baixar. O nome do arquivo fica
    embaixo, que é como a pessoa reconhece o que o cliente mandou.
  */
  if (mensagem.tipo === 'documento' && mensagem.midiaMime === 'video/mp4') {
    return (
      <>
        <PlayerDeVideo
          url={url}
          aoTelaCheiaRecusada={abrir}
          className="mb-1 h-72 w-80 max-w-full"
        />
        <button
          type="button"
          onClick={abrir}
          className="mb-1 block text-left text-xs underline decoration-current/40 underline-offset-2 hover:decoration-current"
        >
          {mensagem.midiaNome ?? 'vídeo'}
        </button>
        {notaDeCompressao}
      </>
    )
  }

  /*
    Documento abre no visualizador, e não numa aba nova.

    O link "nova aba" virava navegação na mesma aba e tirava a pessoa da
    conversa. No visualizador, PDF aparece por dentro; o resto ganha um botão
    de baixar.
  */
  return (
    <button
      type="button"
      onClick={abrir}
      className="mb-1 block text-left underline decoration-current/40 underline-offset-2 hover:decoration-current"
    >
      {mensagem.midiaNome ?? 'documento'}
    </button>
  )
}

/**
 * Trazer o contato da conversa para o CRM.
 *
 * Usa o mesmo formulário do quadro de leads, e não uma versão reduzida: os
 * campos obrigatórios do cadastro (seção 4) são validados lá, e um formulário
 * paralelo criaria lead sem eles — inconsistente com todo o resto da base.
 *
 * A diferença é o seletor de funil, que aparece porque aqui não existe coluna
 * de referência: no quadro o funil vem de onde a pessoa clicou.
 */
function ModalCrm({
  aberto,
  contatoId,
  aoFechar,
}: {
  aberto: boolean
  contatoId: string
  aoFechar: () => void
}) {
  const cliente = useQueryClient()
  const { data: contato } = useContato(aberto ? contatoId : null)
  const { data: funis } = useFunis()
  const novaNegociacao = useNovaNegociacao()
  const { data: campos } = useCampos()
  const { data: tags } = useTags()
  const { data: usuarios } = useEquipe()

  if (!aberto) return null

  // Sem o contato carregado o formulário abriria vazio e sobrescreveria o
  // cadastro com campos em branco ao salvar.
  if (!contato || !funis) {
    return (
      <Modal aberto titulo="Adicionar ao CRM" aoFechar={aoFechar}>
        <Carregando texto="Abrindo o cadastro…" />
      </Modal>
    )
  }

  const fechar = () => {
    // A etiqueta do CRM vive na lista de conversas, que não é invalidada pelo
    // cadastro de contatos — sem isto ela continuaria vermelha.
    void cliente.invalidateQueries({ queryKey: ['conversas'] })
    aoFechar()
  }

  return (
    <ModalContato
      aberto
      contato={contato}
      campos={campos ?? []}
      tags={tags ?? []}
      usuarios={usuarios ?? []}
      funis={funis}
      funilPadrao={funis.find((f) => f.ativo)?.id ?? null}
      titulo={
        contato.negociacoesAbertas > 0
          ? `${contato.nome} no CRM`
          : `Adicionar ${contato.nome} ao CRM`
      }
      /*
        Do bate-papo o caminho para o funil é este botão. O contato já existe
        (chegou pelo WhatsApp), então não há cadastro para criar — o que falta
        é abrir o ciclo, e é o que "Nova negociação" faz.
      */
      aoIniciarNovaNegociacao={(id) => {
        const funil = funis.find((f) => f.ativo)
        if (!funil) return
        novaNegociacao.mutate({ contatoId: id, funilId: funil.id }, { onSuccess: fechar })
      }}
      aoFechar={fechar}
    />
  )
}

/**
 * Ações da conversa, à direita do nome, só com ícone.
 *
 * Com o texto eram oito botões de cerca de 120px que quebravam em duas ou
 * três linhas e empurravam as mensagens para baixo. Como ícone, cabem numa
 * fileira ao lado do nome e o que cada um faz aparece no hover.
 *
 * "Criar negociação" fica em destaque quando o contato ainda não está em
 * funil nenhum: é a única das oito que tem hora certa de ser usada, e quem
 * chega pelo bate-papo some do relatório enquanto ninguém clicar nela.
 */
export function BarraDeAcoes({
  conversa,
  chamado,
  usuario,
  aoEncerrar,
  aoTransferir,
  aoApoiar,
  aoAgendarRetorno,
  aoReabrir,
}: {
  conversa: ConversaDto
  chamado: ChamadoDetalheDto | undefined
  usuario: UsuarioSessao
  aoEncerrar: () => void
  aoTransferir: () => void
  aoApoiar: () => void
  aoAgendarRetorno: () => void
  /** Ausente para quem não pode reabrir: a opção some do cabeçalho. */
  aoReabrir?: () => void
}) {
  const [escolhendoAgendamento, setEscolhendoAgendamento] = useState(false)
  const [fechandoParaMim, setFechandoParaMim] = useState(false)
  /*
    Quem está de apoio na conversa de outra pessoa não encerra nem transfere
    (pedido de 05/10/2026). As duas somem, e no lugar fica "Fechar para mim",
    que tira a pessoa do apoio e deixa a conversa com o dono. O servidor
    confere a mesma regra: esconder aqui é conforto.
  */
  const souApoioAceito = Boolean(
    chamado &&
    chamado.responsavelId !== usuario.id &&
    chamado.apoios.some((a) => a.usuarioId === usuario.id),
  )
  // Pedido ainda sem resposta também não encerra nem transfere: responde pela faixa (ADR 0043).
  const souApoio =
    souApoioAceito ||
    Boolean(
      chamado &&
      chamado.responsavelId !== usuario.id &&
      chamado.pedidosDeApoio.some((a) => a.usuarioId === usuario.id),
    )

  function fecharParaMim() {
    setFechandoParaMim(true)
  }
  const [agendando, setAgendando] = useState(false)
  const [criandoTarefa, setCriandoTarefa] = useState(false)
  const [negociacao, setNegociacao] = useState(false)
  const [etiquetas, setEtiquetas] = useState(false)
  const fechado = chamado?.status === 'fechado'

  /*
    Descritores em vez de JSX solto: é o que permite à barra decidir quais
    ícones cabem e mandar o resto para o menu, com o mesmo rótulo nos dois
    lugares.
  */
  const acoes: AcaoDaBarra[] = [
    {
      chave: 'negociacao',
      rotulo: conversa.noCrm
        ? `Negociação${conversa.etapaNome ? `: ${conversa.etapaNome}` : ''}`
        : 'Criar negociação',
      Icone: IconeNegociacao,
      aoClicar: () => setNegociacao(true),
      /*
        Sem destaque para quem não tem negociação aberta.

        O realce existia para cobrar "põe esse lead no funil". Fora do
        comercial, isso é a conversa inteira do setor: o financeiro veria o
        botão aceso em todo atendimento, e realce que nunca apaga deixa de ser
        realce. A ação continua aqui para quem precisar dela.
      */
    },
    /*
      Um botão para os três agendamentos.

      Eram três ícones seguidos — mensagem, tarefa e retorno —, todos relógios
      parecidos, todos dizendo "agendar". Ocupavam metade da barra para
      oferecer uma escolha que ninguém decora, e a diferença entre eles é a
      parte que importa: uma sai para o cliente e as outras duas não. Ícone
      não explica isso; a janela explica.
    */
    /*
      Etiquetas no cabeçalho, ao lado da negociação: etiquetar é coisa de toda
      conversa, e ter de abrir a ficha só para isso era um passo a mais. Some
      para quem não pode mexer em etiqueta, como a lista da ficha.
    */
    ...(pode(usuario, 'leads.editar')
      ? [
          {
            chave: 'etiquetas',
            rotulo: 'Etiquetas',
            Icone: IconeEtiqueta,
            aoClicar: () => setEtiquetas(true),
          },
        ]
      : []),
    {
      chave: 'agendar',
      rotulo: 'Agendar',
      Icone: IconeMensagemAgendada,
      aoClicar: () => setEscolhendoAgendamento(true),
    },
    {
      chave: 'apoio',
      rotulo: 'Pedir apoio',
      Icone: IconeApoio,
      aoClicar: aoApoiar,
      // Quantas pessoas estão de apoio agora; a janela mostra quem são.
      contador: chamado?.apoios.length ?? 0,
    },
    ...(souApoioAceito
      ? [
          {
            chave: 'fechar-para-mim',
            rotulo: 'Fechar para mim',
            Icone: IconeSair,
            aoClicar: fecharParaMim,
            vermelho: true,
          },
        ]
      : souApoio
        ? []
        : [
            {
              chave: 'transferir',
              rotulo: 'Transferir',
              Icone: IconeTransferir,
              aoClicar: aoTransferir,
            },
          ]),
    ...(souApoio
      ? []
      : fechado
        ? aoReabrir
          ? [
              {
                chave: 'reabrir',
                rotulo: 'Reabrir conversa',
                Icone: IconeReabrir,
                aoClicar: aoReabrir,
              },
            ]
          : []
        : [
            {
              chave: 'encerrar',
              rotulo: 'Encerrar conversa',
              Icone: IconeEncerrar,
              aoClicar: aoEncerrar,
              // Vermelho em repouso: desligar a conversa é a ação sem volta da
              // barra, e ela não pode parecer com as outras sete.
              vermelho: true,
            },
          ]),
  ]

  return (
    <>
      <BarraComTransbordo acoes={acoes} />
      <ModalFecharParaMim
        chamadoId={conversa.chamadoId}
        donoNome={chamado?.responsavelNome ?? null}
        aberto={fechandoParaMim}
        aoFechar={() => setFechandoParaMim(false)}
      />

      <EscolherAgendamento
        aberto={escolhendoAgendamento}
        aoFechar={() => setEscolhendoAgendamento(false)}
        aoEscolher={(opcao) => {
          setEscolhendoAgendamento(false)
          if (opcao === 'mensagem') setAgendando(true)
          else if (opcao === 'tarefa') setCriandoTarefa(true)
          else aoAgendarRetorno()
        }}
      />

      <ModalAgendar aberto={agendando} conversa={conversa} aoFechar={() => setAgendando(false)} />
      <ModalTarefa
        aberto={criandoTarefa}
        conversa={conversa}
        aoFechar={() => setCriandoTarefa(false)}
      />
      <ModalCrm
        aberto={negociacao}
        contatoId={conversa.contatoId}
        aoFechar={() => setNegociacao(false)}
      />
      {etiquetas ? (
        <JanelaDeEtiquetas conversa={conversa} aoFechar={() => setEtiquetas(false)} />
      ) : null}
    </>
  )
}
